import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { agenteIntegradorService } from '@/services/api';
import { AgenteIntegrador, PaginationParams, PaginationResponse } from '@/types';
import { PlusIcon, PencilIcon, TrashIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

export const AgentesIntegradoresPage: React.FC = () => {
  const [filters, setFilters] = useState<PaginationParams>({
    page: 1,
    limit: 10,
  });
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateAgentesIntegradores } = useQueryInvalidation();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();
  const [searchText, setSearchText] = useState<string>("");
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchText || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const { data: agentesData, isLoading, error } = useQuery<PaginationResponse<AgenteIntegrador>>({
    queryKey: ['agentes-integradores', filters],
    queryFn: () => agenteIntegradorService.list(filters),
  });

  useEffect(() => {
    if (error) {
      const message = handleError(error as any, 'Erro ao carregar agentes integradores');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (agentesData) {
      setLoadError(null);
    }
  }, [agentesData]);

  const deleteAgenteMutation = useMutation({
    mutationFn: agenteIntegradorService.delete,
    onSuccess: (_data, deletedId) => {
      invalidateAgentesIntegradores(deletedId);
      handleSuccess('Agente integrador excluído com sucesso!');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao excluir agente integrador');
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id: number) => {
    showConfirm(
      'Excluir Agente Integrador',
      'Tem certeza que deseja excluir este agente integrador? Esta ação não pode ser desfeita.',
      async () => {
        await deleteAgenteMutation.mutateAsync(id);
      },
      'danger'
    );
  };

  if (loadError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agentes Integradores</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os agentes integradores cadastrados no sistema
          </p>
        </div>
        <Link to="/agentes-integradores/novo">
          <Button>
            <PlusIcon className="h-4 w-4 mr-2" />
            Novo Agente
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Lista de Agentes Integradores</CardTitle>
              <CardDescription>
                {agentesData?.pagination.total || 0} agentes encontrados
              </CardDescription>
            </div>
            <div className="w-full md:w-72 relative">
              <input
                type="text"
                placeholder="Buscar por nome"
                className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchText("")}
                >
                  x
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : agentesData?.data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhum agente integrador encontrado</p>
              <Link to="/agentes-integradores/novo">
                <Button className="mt-4">Cadastrar primeiro agente</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {agentesData?.data.map((agente: AgenteIntegrador) => (
                <div
                  key={agente.id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 p-2 bg-blue-100 rounded-md">
                        <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{agente.nome}</h3>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Link to={`/agentes-integradores/${agente.id}/editar`}>
                        <Button variant="outline" size="sm" className="text-yellow-600 hover:text-yellow-700 hover:border-yellow-300" title="Editar">
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(agente.id)}
                        disabled={deleteAgenteMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                        title="Excluir"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {agentesData && agentesData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={!agentesData.pagination.hasPrev}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-sm text-gray-700">
                  Página {agentesData.pagination.page} de {agentesData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={!agentesData.pagination.hasNext}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteAgenteMutation.isPending}
      />
    </div>
  );
};
